import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import dayjs from 'dayjs';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import type {
  NotificationActionDto,
  UnreadNotification
} from 'api/messageCommunication/types';

import { LikeButtonSVG, RejectButtonSVG } from 'components/shared/Icons';
import useUpdateTicketStatus from 'components/actions/ChangeStatus/hooks/useUpdateTicketStatus';
import { AccessElements, NotificationKey } from 'helpers/app.config';
import { useGlobalContext } from 'context/GlobalContext';
import ModalSendFormSurveys from 'pages/Surveys/components/ModalSendForm';

import { isLegacyStatusNotificationKey } from '../helpers/legacyStatusNotificationKeys';
import useNotificationActions, {
  DestinyStatus
} from '../hooks/useNotificationActions';
import {
  looksLikeRichNotificationHtml,
  renderSafeNotificationHtml
} from '../utils/renderSafeNotificationHtml';

const styles = {
  root: {
    display: 'flex',
    padding: '16px',
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '0.5px solid #ACACAC',
    flexDirection: {
      xs: 'column',
      sm: 'row'
    },
    justifyContent: 'space-between'
  },
  avatarContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  icon: {
    marginRight: '16px',
    marginBottom: {
      xs: '10px',
      sm: '0px'
    }
  },
  buttonBase: {
    marginTop: 2 * 8,
    cursor: 'pointer'
  },
  doneIcon: {
    position: 'absolute',
    top: 8,
    right: 8,
    color: '#1D6FCC'
  },
  dateContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: {
      xs: 'center',
      sm: 'flex-end'
    },
    marginTop: {
      xs: '10px',
      sm: '0px'
    },
    minWidth: '80px'
  },
  actionsButtonsContainer: {
    flex: '1',
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap' as const,
    gap: '4px',
    justifyContent: 'flex-end'
  },
  configuratorActionButton: {
    fontSize: '11px',
    textTransform: 'none' as const,
    minWidth: 'auto',
    padding: '4px 10px'
  },
  btnSendSurvey: {
    fontSize: '12px',
    background: '#1d6fcc',
    borderRadius: '8px',
    color: 'white',
    position: 'absolute' as const,
    right: '30px'
  },
  commentField: {
    width: '100%',
    marginTop: '12px'
  }
};

type Props = Partial<UnreadNotification> & {
  handleReadNotification: (notificationId: number) => void;
  notificationKey: NotificationKey | string;
  needsAttention: boolean;
  ticketIdActive: number;
  setTicketId: Dispatch<SetStateAction<number | string>>;
  setNotificationId: Dispatch<SetStateAction<number>>;
  setDestinyStatus: Dispatch<SetStateAction<DestinyStatus>>;
  handleClose: () => void;
  setOpenLogoutModal?: () => void;
};

type ConfiguratorPayload = {
  ruleId?: number;
  entityType?: string;
  entityId?: number;
  actions?: NotificationActionDto[];
};

function splitByTags(text: string): string[] {
  const tagsContent: string[] = [];
  const regex = /<b>(.*?)<\/b>/g;
  let match;

  let start = 0;

  // eslint-disable-next-line no-cond-assign
  while ((match = regex.exec(text)) !== null) {
    const tagContent = match[1];
    const startOfMatch = text?.indexOf(match[0], start);

    if (startOfMatch > start) {
      tagsContent.push(text?.substring(start, startOfMatch));
    }

    tagsContent.push(tagContent);

    start = startOfMatch + match[0].length;
  }

  if (start < text?.length) {
    tagsContent.push(text?.substring(start));
  }

  return tagsContent;
}

function parseConfiguratorPayload(
  objects?: string | ConfiguratorPayload
): ConfiguratorPayload {
  if (!objects) return {};

  if (typeof objects === 'object') {
    return objects;
  }

  try {
    return JSON.parse(objects) as ConfiguratorPayload;
  } catch {
    return {};
  }
}

/**
 * Notification Component
 *
 * Represents a notification component that displays information and actions for a notification.
 *
 * @component
 * @param {Object} Props The properties for the notification component.
 * @param {Function} Props.advise The advice message for the notification.
 * @param {Date} Props.emmitedDate The date when the notification was emitted.
 * @param {Date} Props.expireDate The expiration date for the notification.
 * @param {Function} Props.handleClose Function to handle closing the notification.
 * @param {Function} Props.handleReadNotification Function to handle marking the notification as read.
 * @param {string} Props.iconUrl The URL for the notification icon.
 * @param {string} Props.message The main message content of the notification.
 * @param {boolean} [Props.needsAttention=true] Flag indicating if the notification needs attention.
 * @param {number} Props.notificationId The unique identifier for the notification.
 * @param {NotificationKey}[Props.notificationKey=NotificationKey.Survey] The key identifying the type of notification.
 * @param {number} Props.ticketIdActive The active ticket ID associated with the notification.
 * @param {Dispatch} Props.setDestinyStatus Function to set the destiny status for the notification.
 * @param {Dispatch} Props.setNotificationId Function to set the notification ID.
 * @param {Dispatch} Props.setTicketId Function to set the ticket ID.
 * @param {string} Props.title  The title of the notification.
 * @param {Function} Props.setOpenLogoutModal Function to open logout modal
 * @returns {JSX.Element} The rendered notification component.
 */
function Notification({
  actions: actionsProp,
  advise,
  emmitedDate,
  entityId: entityIdProp,
  expireDate,
  handleClose,
  handleReadNotification,
  iconUrl,
  message,
  needsAttention,
  notificationId,
  notificationKey,
  objects,
  ticketIdActive,
  setDestinyStatus,
  setNotificationId,
  setTicketId,
  setOpenLogoutModal,
  title
}: Props): JSX.Element {
  const { hasPerm } = useGlobalContext();

  const [hasPermissionForAccept, setHasPermissionForAccept] = useState(false);
  const [hasPermissionForReject, setHasPermissionForReject] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [ticketIdSelected, setTicketIdSelected] = useState<number>(undefined);
  const [isCreateAuditModalOpen, setCreateAuditModal] = useState(false);
  const [pendingAction, setPendingAction] =
    useState<NotificationActionDto | null>(null);
  const [comment, setComment] = useState('');

  const { t } = useTranslation('global');

  const configuratorFromObjects = useMemo(
    () => parseConfiguratorPayload(objects),
    [objects]
  );

  const resolvedActions = useMemo(
    () => actionsProp ?? configuratorFromObjects.actions ?? [],
    [actionsProp, configuratorFromObjects.actions]
  );

  const hasConfiguratorActions = resolvedActions.length > 0;

  const resolvedEntityId =
    entityIdProp ?? configuratorFromObjects.entityId ?? ticketIdActive;

  const canShowConfiguratorAction = useCallback(
    (permissionKey?: string | null) => {
      if (!permissionKey) return true;
      return hasPerm([permissionKey as AccessElements]);
    },
    [hasPerm]
  );

  const visibleConfiguratorActions = useMemo(
    () =>
      resolvedActions.filter((action) =>
        canShowConfiguratorAction(action.permissionKey)
      ),
    [resolvedActions, canShowConfiguratorAction]
  );

  useEffect(() => {
    if (hasConfiguratorActions) {
      setHasPermissionForAccept(false);
      setHasPermissionForReject(false);
      return;
    }

    let acept: AccessElements;
    if (notificationKey === NotificationKey.TicketReviewPendingSerie) {
      acept = AccessElements.TicketPendingBySerie;
    } else if (notificationKey === NotificationKey.ReviewPendingWarehouse) {
      acept = AccessElements.TicketPendingByWarehouse;
    } else if (notificationKey === NotificationKey.ReviewScheduled) {
      acept = AccessElements.TicketUpdateToScheduled;
    }

    setHasPermissionForAccept(hasPerm([acept]));
    if (
      notificationKey === NotificationKey.TicketReviewPendingSerie ||
      notificationKey === NotificationKey.ReviewPendingWarehouse
    ) {
      setHasPermissionForReject(
        hasPerm([AccessElements.TicketUpdateToOnSiteStatus])
      );
    } else if (notificationKey === NotificationKey.ReviewScheduled) {
      setHasPermissionForReject(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationKey, hasConfiguratorActions]);

  const handleClick = useCallback(() => {
    if (
      notificationKey === NotificationKey.UserRoleChanged &&
      setOpenLogoutModal
    ) {
      setOpenLogoutModal();
    }
    handleReadNotification(notificationId);
  }, [
    handleReadNotification,
    notificationId,
    notificationKey,
    setOpenLogoutModal
  ]);

  // Convertir fecha UTC del mensaje a hora local del cliente
  const convertMessageWithLocalDate = (originalMessage: string) => {
    if (!originalMessage) return originalMessage;

    // Buscar el patrón de fecha en formato DD/MM/YYYY HH:mm dentro de tags <b>
    const datePattern = /<b>(\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2})<\/b>/g;
    const matches = [...originalMessage.matchAll(datePattern)];

    // Si hay fechas en el mensaje, convertir la última (que debería ser scheduledDate)
    if (matches.length > 0) {
      const lastMatch = matches[matches.length - 1];
      const dateString = lastMatch[1]; // "26/11/2025 20:01"

      try {
        // Parsear la fecha como UTC y convertir a hora local
        const [datePart, timePart] = dateString.split(' ');
        const [day, month, year] = datePart.split('/');
        const [hour, minute] = timePart.split(':');

        // Crear fecha UTC
        const utcDate = dayjs.utc(
          `${year}-${month}-${day}T${hour}:${minute}:00Z`
        );

        // Convertir a hora local y formatear
        const localDate = utcDate.local().format('DD/MM/YYYY HH:mm');

        // Reemplazar en el mensaje
        return originalMessage.replace(lastMatch[0], `<b>${localDate}</b>`);
      } catch (error) {
        // Si falla el parseo, retornar el mensaje original
        return originalMessage;
      }
    }

    return originalMessage;
  };

  const messageWithLocalDate = convertMessageWithLocalDate(message);

  const isConfiguratorNotification =
    notificationKey === NotificationKey.CustomNotificationConfigurator ||
    !!configuratorFromObjects.ruleId;

  const useRichHtmlMessage =
    looksLikeRichNotificationHtml(messageWithLocalDate) ||
    (isConfiguratorNotification && looksLikeRichNotificationHtml(message));

  const richHtmlContent = useMemo(() => {
    if (!useRichHtmlMessage || !messageWithLocalDate) return null;
    return renderSafeNotificationHtml(messageWithLocalDate);
  }, [messageWithLocalDate, useRichHtmlMessage]);

  const [
    firstText,
    ticketId,
    secondText,
    clientText,
    thirdText,
    statusText,
    fourthText,
    scheduledDate
  ] = useRichHtmlMessage
    ? ['', '', '', '', '', '', '', '']
    : splitByTags(messageWithLocalDate);

  const ticketIdForAction =
    Number(resolvedEntityId) || Number(ticketId) || ticketIdActive;

  const { dispatchAction } = useNotificationActions({
    ticketId: ticketIdForAction,
    setTicketId,
    setNotificationId,
    notificationId,
    destinyStatus: null,
    setDestinyStatus,
    handleClose,
    handleReadNotification
  });

  const updateTicketStatus = useUpdateTicketStatus();

  const settleConfiguratorAction = useCallback(() => {
    setDisabled(false);
    setPendingAction(null);
    setComment('');
  }, []);

  /**
   * Acciones de cambio de estatus: TicketApi (legacy mapeado o updateTicketStatus).
   * No usa notification-actions/execute (deprecado para ChangeTicketStatus).
   */
  const executeTicketStatusAction = useCallback(
    (action: NotificationActionDto, actionComment: string | null = null) => {
      if (!ticketIdForAction || !notificationId) {
        setDisabled(false);
        return;
      }

      if (isLegacyStatusNotificationKey(notificationKey)) {
        setDisabled(true);
        setNotificationId(notificationId);
        setTicketId(ticketIdForAction);
        dispatchAction(
          notificationKey as NotificationKey,
          action.actionKey === 'approve'
        );
        setPendingAction(null);
        setComment('');
        // Rechazo abre GeneralModalChangeStatus; liberar el botón de la campanita.
        if (action.actionKey !== 'approve') {
          setDisabled(false);
        }
        return;
      }

      if (!action.targetStatusId) {
        setDisabled(false);
        return;
      }

      setDisabled(true);
      updateTicketStatus.mutate(
        {
          ticketId: ticketIdForAction,
          finalStatusId: action.targetStatusId,
          comment: actionComment ?? undefined
        },
        {
          onSuccess: () => {
            handleReadNotification(notificationId);
            handleClose();
          },
          onSettled: settleConfiguratorAction
        }
      );
    },
    [
      dispatchAction,
      handleClose,
      handleReadNotification,
      notificationId,
      notificationKey,
      setNotificationId,
      setTicketId,
      settleConfiguratorAction,
      ticketIdForAction,
      updateTicketStatus
    ]
  );

  const handleConfiguratorActionClick = useCallback(
    (action: NotificationActionDto) => {
      // Legacy: el rechazo pide comentario en GeneralModalChangeStatus, no en este Dialog.
      if (
        isLegacyStatusNotificationKey(notificationKey) ||
        !action.requiresComment
      ) {
        executeTicketStatusAction(action, null);
        return;
      }

      setPendingAction(action);
      setComment('');
    },
    [executeTicketStatusAction, notificationKey]
  );

  const handleConfirmComment = useCallback(() => {
    if (!pendingAction || !comment.trim()) return;
    executeTicketStatusAction(pendingAction, comment.trim());
  }, [comment, executeTicketStatusAction, pendingAction]);

  const handleCancelComment = useCallback(() => {
    setPendingAction(null);
    setComment('');
    setDisabled(false);
  }, []);

  const activeForm = () => {
    setTicketIdSelected(ticketIdActive);
    setCreateAuditModal(true);
  };

  const handleIsCreateChange = (item: boolean) => {
    if (item) {
      setCreateAuditModal(false);
      handleClick();
    }
  };

  return (
    <Box
      onClick={
        !needsAttention
          ? notificationKey !== NotificationKey.Survey && handleClick
          : undefined
      }
      style={styles.buttonBase}
    >
      <Box sx={styles.root}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'row'
            }
          }}
        >
          <Box sx={styles.avatarContainer}>
            <Avatar src={iconUrl} sx={styles.icon} />
          </Box>
          <Box
            sx={{
              textAlign: {
                xs: 'justify',
                sm: 'initial'
              }
            }}
          >
            <Typography
              fontSize="12px"
              letterSpacing="0.4px"
              variant="body2"
              gutterBottom
            >
              <strong>{title}</strong>
            </Typography>

            {useRichHtmlMessage ? (
              <Typography
                component="div"
                fontSize="12px"
                letterSpacing="0.4px"
                variant="body2"
                gutterBottom
                sx={{
                  whiteSpace: 'normal',
                  '& p': { m: 0, mb: 0.5 },
                  '& ul, & ol': { m: 0, pl: 2 },
                  '& li': { mb: 0.25 }
                }}
              >
                {richHtmlContent}
              </Typography>
            ) : hasConfiguratorActions ? (
              <Typography
                fontSize="12px"
                letterSpacing="0.4px"
                variant="body2"
                gutterBottom
                sx={{ whiteSpace: 'pre-wrap' }}
              >
                {message}
              </Typography>
            ) : (
              <Typography
                fontSize="12px"
                letterSpacing="0.4px"
                variant="body2"
                gutterBottom
              >
                {firstText} <strong>{ticketId}</strong> {secondText}{' '}
                <strong>{clientText}</strong> {thirdText}{' '}
                <strong>{statusText}</strong>
                {fourthText && (
                  <>
                    {fourthText.split('<br/>').map((line, index, array) => (
                      <span key={index}>
                        {line}
                        {index < array.length - 1 && <br />}
                      </span>
                    ))}{' '}
                    {scheduledDate && <strong>{scheduledDate}</strong>}
                  </>
                )}
              </Typography>
            )}

            {!hasConfiguratorActions &&
              (!expireDate && !ticketId ? (
                <div />
              ) : (
                notificationKey !== NotificationKey.Survey && (
                  <Typography fontSize="12px" variant="body2">
                    {t('drawer.surveys.dateFinish')}{' '}
                    {expireDate
                      ? dayjs.utc(expireDate).local().format('DD-MM-YYYY')
                      : '---'}
                  </Typography>
                )
              ))}

            {needsAttention && advise && (
              <Typography style={{ marginTop: 4 }} variant="body2">
                {advise}
              </Typography>
            )}

            {notificationKey === NotificationKey.Survey && (
              <Box style={styles.actionsButtonsContainer}>
                <Typography fontSize="12px" variant="body2">
                  {t('drawer.surveys.reportSurveys')}
                </Typography>
                <IconButton
                  onClick={() => {
                    activeForm();
                  }}
                  style={styles.btnSendSurvey}
                >
                  {t('drawer.surveys.toAnswer')}
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={styles.dateContainer}>
          <Box
            sx={{
              display: {
                xs: 'flex',
                sm: 'grid'
              },
              flexDirection: {
                xs: 'row-reverse'
              }
            }}
          >
            <Typography variant="body2" gutterBottom>
              {dayjs.utc(emmitedDate).local().format('HH:mm:ss')} hrs
            </Typography>

            <Typography
              sx={{ marginRight: { xs: '10px', sm: '0px' } }}
              variant="body2"
              gutterBottom
            >
              {dayjs.utc(emmitedDate).local().format('DD-MM-YYYY')}
            </Typography>
          </Box>
          {needsAttention && hasConfiguratorActions && (
            <Box style={styles.actionsButtonsContainer}>
              {visibleConfiguratorActions.map((action) => (
                <Button
                  key={action.actionKey}
                  color="primary"
                  disabled={disabled || updateTicketStatus.isLoading}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleConfiguratorActionClick(action);
                  }}
                  size="small"
                  style={styles.configuratorActionButton}
                  variant={
                    action.actionKey === 'reject' ? 'outlined' : 'contained'
                  }
                >
                  {action.label}
                </Button>
              ))}
            </Box>
          )}
          {needsAttention && !hasConfiguratorActions && (
            <Box style={styles.actionsButtonsContainer}>
              {hasPermissionForReject && (
                <IconButton
                  disabled={disabled}
                  onClick={() => {
                    setDisabled(true);
                    dispatchAction(notificationKey as NotificationKey, false);
                  }}
                >
                  <LikeButtonSVG />
                </IconButton>
              )}
              {hasPermissionForAccept && (
                <IconButton
                  disabled={disabled}
                  onClick={() => {
                    setDisabled(true);
                    dispatchAction(notificationKey as NotificationKey, true);
                  }}
                >
                  <RejectButtonSVG />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </Box>
      <ModalSendFormSurveys
        isCreateAuditModalOpen={isCreateAuditModalOpen}
        onCreateChange={(isCreate: boolean) => handleIsCreateChange(isCreate)}
        sendTicketId={ticketIdSelected}
        setCreateAuditModal={setCreateAuditModal}
      />
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={handleCancelComment}
        open={!!pendingAction}
      >
        <DialogTitle>
          {pendingAction?.label || t('common.commonActions.accept')}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom variant="body2">
            {`* ${t('common.commonProperties.comment')}`}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            onChange={(event) => setComment(event.target.value)}
            style={styles.commentField}
            value={comment}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={updateTicketStatus.isLoading}
            onClick={handleCancelComment}
            variant="outlined"
          >
            {t('common.commonActions.cancel')}
          </Button>
          <Button
            disabled={
              !comment.trim() || disabled || updateTicketStatus.isLoading
            }
            onClick={handleConfirmComment}
            variant="contained"
          >
            {pendingAction?.label || t('common.commonActions.accept')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default memo(Notification);
